import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { IconBrandWhatsapp, IconShoppingCart } from '@tabler/icons-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import NavbarCategories from './Navbarcategories';
import ClientsSection from './Ourclients';

interface Product {
  id: string;
  m06_thumbnail_image: string;
  m06_product_sku_name: string;
  m06_description: string;
  m06_price: number;
  m06_mrp: number;
}

const ProductsSection: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const whatsappNumber = '+971505411910';

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL_CUSTOMER}/product-sku`,
          { params: { page: 1, limit: 8 } }
        );

        if (response.data?.success) {
          setProducts(response.data.data.products_skus || []);
          setError(null);
        } else {
          throw new Error('Failed to fetch products');
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        setError('Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setShowLeftScroll(container.scrollLeft > 0);
      setShowRightScroll(
        container.scrollLeft + container.clientWidth < container.scrollWidth
      );
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = direction === 'left' ? -280 : 280;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      // Initial check
      checkScroll();
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScroll);
      }
    };
  }, [products]);

  if (loading) return <p className="text-center py-10">Loading products...</p>;
  if (error) return <p className="text-center py-10 text-red-500">{error}</p>;

  return (
    <div className="pt-5 bg-gray-50 relative font-sfui">
      <div className="py-5 z-40">
        <NavbarCategories />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-10 pt-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Featured Products</h2>
        
        {/* Scroll Navigation Buttons */}
        <div className="relative">
          {showLeftScroll && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-40 bg-white rounded-full p-2 shadow-lg"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}
          {showRightScroll && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-40 bg-white rounded-full p-2 shadow-lg"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4"
          >
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-gray-100 rounded-lg overflow-hidden cursor-pointer snap-start transition-transform duration-300 hover:shadow-md"
                onClick={() => navigate(`/product/${product.id}`)}
              >
                {/* Product Card - Matching the design from the image */}
                <div className="p-4 flex flex-col h-full">
                <div className="flex-grow">
                    <h3 className="text-gray-700 font-bold mb-1 truncate">
                      {product.m06_product_sku_name}
                    </h3>
                    <h3 className="text-gray-700 font-medium mb-1 text-[10px] truncate">
                      {product.m06_product_sku_name}
                    </h3>
                    
                    {/* <p className="text-gray-900 font-bold">
                      ${product.m06_price.toFixed(2)}
                    </p> */}
                  </div>
                  <div className="relative  bg-gray-100 rounded-lg pt-4 flex items-center justify-center">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/${product.m06_thumbnail_image}`}
                      alt={product.m06_product_sku_name}
                      className="w-full h-40 object-contain"
                    />
                    {/* <div className="absolute top-2 right-2">
                      <IconShoppingCart size={20} className="text-gray-700" />
                    </div> */}
                  </div>
                  
              
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="text-right mb-6 mr-4">
        <button 
          onClick={() => navigate('/allproducts')}
          className="px-3 py-2 bg-[#1f293715] text-gray-700 rounded-md hover:bg-[#1f29373d] transition-colors"
        >
          View All Products
        </button>
      </div>
      
      <ClientsSection/>
    </div>
  );
};

export default ProductsSection;